import { HttpErrorResponse } from '@angular/common/http'
import { ProfileEntity } from 'libs/gb-front/selected-profile/data-access/src'
import { Action, createAction, props } from '@ngrx/store'

import {
  AuthenticationEntity,
  ChangePasswordPayload,
  forgotPasswordPayload,
  RegisterExtras,
  RegisterPayload,
  RegisterVolunteerPayload,
  SendVerificationPayload,
  ShortUrl,
  VerificationPayload,
  verifyForgotPasswordPayload,
  volunteerLoginPayload,
} from './auth.models'
import { Update } from '@ngrx/entity'

export const openRegisterLoginDialog = createAction(
  '[Item] Open Register Login Dialog',
  props<{ checkProfile: boolean; registerExtras?: RegisterExtras; onSuccess?: () => Action }>(),
)

export const openRegisterVerifyDialog = createAction(
  '[Authentication] Open Register Verify Dialog',
  (onSuccess?: () => Action, registerExtras?: RegisterExtras) => ({ onSuccess, registerExtras }),
)

export const syncCurrentUser = createAction(
  '[Authentication/API] Sync Current User',
  (urlName: string, redirectHome = false) => ({
    urlName,
    redirectHome,
  }),
)

export const syncCurrentUserFailure = createAction(
  '[Authentication] Sync Current User Failure',
  props<{ error: HttpErrorResponse }>(),
)

// register

export const register = createAction(
  '[Authentication] Register',
  props<{ payload: RegisterPayload; dialog?: boolean; registerExtras?: RegisterExtras }>(),
)

export const registerSuccess = createAction(
  '[Authentication] Registration Success',
  props<{ identity: string; uuid: string; dialog?: boolean; registerExtras?: RegisterExtras }>(),
)

export const registerFailure = createAction(
  '[Authentication] Registration Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const registerByVolunteer = createAction(
  '[Authentication] Register by Volunteer',
  props<{ payload: RegisterVolunteerPayload; dialog: boolean }>(),
)

export const registerByVolunteerSuccess = createAction(
  '[Authentication] Registration by Volunteer Success',
  props<{ identity: string; uuid: string; dialog: boolean }>(),
)

// verify
export const verify = createAction(
  '[Authentication] Verify',
  props<{
    payload: VerificationPayload
    dialog?: boolean
    onSuccess?: () => Action
  }>(),
)

export const verifyToken = createAction('[Authentication] Verify Token', props<{ token: string }>())

export const verifySuccess = createAction(
  '[Authentication] Verify Success',
  props<{
    authentication: AuthenticationEntity
    dialog?: boolean
    onSuccess?: () => Action
  }>(),
)

export const verifyFailure = createAction(
  '[Authentication] Verification Failure',
  props<{ error: HttpErrorResponse }>(),
)

export const clearVerifyError = createAction('[Authentication] Clear Verify Error')

// Verify phone to receive notifications
export const verifyNotification = createAction('[Authentication] Verify Notification', props<{ token: string }>())

export const verifyNotificationSuccess = createAction(
  '[Authentication] Verify Notification Success',
  props<{ authentication: AuthenticationEntity }>(),
)

export const verifyNotificationFailure = createAction(
  '[Authentication] Verify Notification Failure',
  props<{ error: HttpErrorResponse }>(),
)

// Resend verification email or sms
export const sendVerification = createAction(
  '[Authentication] Send Verification',
  props<{ uuid: string; payload?: SendVerificationPayload }>(),
)

export const sendVerificationSuccess = createAction('[Authentication] Send Verification Success')

export const sendVerificationFailure = createAction(
  '[Authentication] Send Verification Failure',
  props<{ error: HttpErrorResponse }>(),
)

// Short code authentication (checkout and vouchers)
export const verifyShortCode = createAction('[Authentication] Verify short code', props<{ code: string }>())

export const navigateToShortCodeUrl = createAction(
  '[Authentication] Navigate to shortcode Url',
  props<{ actualUrl: string }>(),
)

export const verifyShortCodeSuccess = createAction(
  '[Authentication] Verify short code success',
  props<{ shortCode: ShortUrl }>(),
)

export const verifyShortCodeFailure = createAction(
  '[Authentication] Verify short code failure',
  props<{ error: HttpErrorResponse }>(),
)

export const requestAditionalInformation = createAction(
  '[Authentication] Request Additional Information',
  props<AuthenticationEntity>(),
)

// volunteer login

export const volunteerLogin = createAction(
  '[Authentication] Volunteer Login',
  props<{ payload: volunteerLoginPayload }>(),
)

export const volunteerLoginSuccess = createAction(
  '[Authentication] Volunteer Login Success',
  props<{ authentication: AuthenticationEntity }>(),
)

// guest login

export const login = createAction(
  '[Authentication] Login',
  props<{
    identity: string
    password: string
    redirectHome?: boolean
    dialog?: boolean
  }>(),
)

export const loginSuccess = createAction(
  '[Authentication] Login Success',
  props<{
    authentication: AuthenticationEntity
    redirectHome?: boolean
    dialog?: boolean
  }>(),
)

export const loginFailed = createAction('[Authentication] Login Failed', props<{ error: HttpErrorResponse }>())

export const loginCompleted = createAction('[Authentication] Completed') // When all login process has finished.

// sets authentication - called after verification and after login

export const setAuthentication = createAction(
  '[Authentication] Set Authentication',
  props<{
    authentication: AuthenticationEntity
    redirectHome?: boolean
  }>(),
)

export const setAuthenticationSuccess = createAction('[Authentication] Set Authentication Success')

// logout

export const logout = createAction('[Authentication] Logout', (redirectHome: boolean = true) => ({ redirectHome }))

export const logoutSuccess = createAction('[Authentication] Logout Success', (redirectHome: boolean = true) => ({
  redirectHome,
}))

// update Profile

export const updateAuthProfile = createAction(
  '[Authentication] Update AuthProfile',
  props<{ update: Update<ProfileEntity> }>(),
)

export const checkProfileComplete = createAction('[Authentication] Check Profile Complete')

export const setAutenticationNoRedirect = createAction(
  '[Authentication] Set Login User Auth Info No Redirect to Home Page',
  props<{ authentication: AuthenticationEntity }>(),
)

export const socialLogin = createAction(
  '[Authentication] Social Login',
  props<{
    accessToken: string
    provider: string
    redirectHome?: boolean
    dialog?: boolean
    onSuccess?: Action
  }>(),
)

export const socialLoginSuccess = createAction(
  '[Authentication] Social login success',
  props<{
    authentication: AuthenticationEntity
    dialog?: boolean
  }>(),
)

export const forgotPassword = createAction(
  '[Authentication] Forgot password',
  props<{ payload: forgotPasswordPayload }>(),
)

export const forgotPasswordSuccess = createAction('[Authentication] Forgot password Success')

export const forgotPasswordFailure = createAction('[Authentication] Forgot password failure')

export const verifyForgotPassword = createAction(
  '[Authentication] Verify forgot password',
  props<{ payload: verifyForgotPasswordPayload }>(),
)

export const verifyForgotPasswordSuccess = createAction(
  '[Authentication] Verify forgot password Success',
  props<{ authentication: AuthenticationEntity }>(),
)

export const verifyForgotPasswordFailure = createAction('[Authentication] Verify forgot password failure')

// change password
export const changePassword = createAction(
  '[Authentication] Change password',
  props<{ payload: ChangePasswordPayload; onSuccess?: () => void }>(),
)

export const changePasswordSuccess = createAction(
  '[Authentication] Change password success',
  props<{ onSuccess?: () => void }>(),
)

export const changePasswordFailure = createAction(
  '[Authentication] Change password failure',
  props<{ error: HttpErrorResponse }>(),
)

export const resendPhoneNumberVerification = createAction(
  '[Authentication] Resend phone number verification',
  props<{ profileUuid: string }>(),
)

export const resendPhoneNumberVerificationSuccess = createAction(
  '[Authentication] Resend phone number verification success',
)

export const resendPhoneNumberVerificationFailure = createAction(
  '[Authentication] Resend phone number verification failure',
  props<{ error: HttpErrorResponse }>(),
)

export const updateToken = createAction(
  '[Authentication] Update token',
  props<{ token: string; refresh_token?: string }>(),
)

export const loginWithShortLink = createAction('[Authentication] Login with short link', props<{ urlName: string }>())

export const checkPromptLogin = createAction('[Authentication] Check Prompt Login', props<{ title?: string }>())

export const openLoginToProceedDialog = createAction(
  '[Authentication] Open login to proceed dialog',
  props<{ title?: string }>(),
)

// refresh token

export const refreshToken = createAction('[Authentication] Refresh Token')

export const refreshTokenSuccess = createAction(
  '[Authentication] Refresh Token Success',
  props<{ authentication: AuthenticationEntity; onSuccess?: (token: string, refresh_token: string) => void }>(),
)

export const refreshTokenFailure = createAction(
  '[Authentication] Refresh Token Failure',
  props<{ error: HttpErrorResponse }>(),
)
